import React, { Component } from 'react';
import { Container, Popover, Button, TextField, Paper, Typography, Tooltip, FormControlLabel, Checkbox, Grid } from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete';
import { List, ListItem, ListItemText, ListItemSecondaryAction, IconButton, Divider } from '@material-ui/core';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline';

import { ThemeColors } from '../Theme';
import { AgsUser } from '../models/AgsUser';
import { Global } from '../models/Global';
import { Permissions, PermissionSet, EmployeeOnlyPermissions, PermissionDescriptions, PopupMenu } from 'react-frontend-utils';




function PermissionCheckboxes(props) {
    
    const checkboxes = [];
    Object.keys(Permissions).forEach(key => {
        if (props.isEmployee || !EmployeeOnlyPermissions.includes(key))  // show checkbox for employees, or if not an employee, show only if its not in the EmployeeOnlyPermissions
            checkboxes.push({name: key, label: key.replaceAll("_", " "), isSelected: props.selected.includes(key), tooltip: PermissionDescriptions[key]});        
    });
    
    return (
        <div style={{marginBottom: 15}}>
            {checkboxes.map((checkbox, index) => 
                <div>
                    {checkbox.name === "PRINT" ? <hr style={{marginRight: 15}}/> : null}
                    <Tooltip key={index} title={checkbox.tooltip ? checkbox.tooltip : ""}>
                        <FormControlLabel key={index} style={{marginBottom: -5}}
                                          control={(<Checkbox size="medium" color='primary' disabled={props.disabled} checked={checkbox.isSelected} onChange={(event) => props.checkboxChanged(checkbox.name, event.target.checked)} />)} 
                                            labelPlacement="end"
                                            label={(<Typography variant="body2" style={{fontSize: 12, marginTop: 2}} color="textSecondary">{checkbox.label}</Typography>)}
                                        />
                    </Tooltip>   
                </div>
            )}
        </div>
    );
}



/**
 * Create a new account with and manage basic settings or edit an existing account's basic settings.
 * 
 * Props:
 * isOpen: boolean - whether the popover is open
 * user: object - AgsUser to edit, or null if creating a new user
 * okCallback: function - called when the user presses the OK button, with the updated user and a boolean indicating if it is a new user
 * cancelCallback: function - called when the user presses the Cancel button
 */
export class EditUserPopover extends Component {

    styles = {
        button: {
            marginLeft: 10,
            marginRight: 10
        },
        container: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginTop: 20,
            marginBottom: 10
        },
        divider: {
            marginLeft: 10,
            marginRight: 10
        },
        paper: {
            width: '100%',
            height: '100%'
        },
        paperLabel: {
            marginLeft: 15,
            color: 'gray',
            fontSize: '9pt',
            flexGrow: 1
        },
        allDatabases: {
            marginTop: 10,
            marginLeft: 15,
            marginBottom: 15,
            paddingBottom: 15,
            fontSize: '12pt',
            flexGrow: 1
        },
    };

 
    _okCallback;
    _cancelCallback;

    state = { };
      
    constructor(props) {
        super(props);
        this._okCallback = props.okCallback;
        this._cancelCallback = props.cancelCallback;
        if (props.user) {
            this.state.lastName = props.user.lastName;
            this.state.firstName = props.user.firstName;
            this.state.email = props.user.email;
            this.state.role = props.user.role;
            this.state.permissionBits = BigInt(props.user.permissionBits);
            this.state.databases = props.user.groups ? [...props.user.groups] : [];
            this.state.profileUrl = props.user.profileUrl; 
            this.state.preferredLocation = props.user.preferredLocation;
        }
        else {
            this.state.lastName = "";
            this.state.firstName = "";
            this.state.email = "";
            this.state.role = AgsUser.Role.ATTENDANT;
            this.state.permissionBits = PermissionSet.defaultPermissionsForRole(this.state.role.enumName);
            this.state.databases = [];
            this.state.profileUrl = ""; 
            this.state.preferredLocation = "";
        }
       
    }
   

    _okButtonPressed = () => {

        const isNew = this.props.user ? false : true;

        const updatedUser = isNew ? new AgsUser() : this.props.user.copyOf();       // make a copy of the user if editing an existing user
        updatedUser.lastName = this.state.lastName;
        updatedUser.firstName = this.state.firstName;
        updatedUser.email = this.state.email;
        updatedUser.role = this.state.role;
        updatedUser.permissionBits = this.state.permissionBits;
        updatedUser.groups = this.state.databases;
        updatedUser.profileUrl = this.state.profileUrl;
        updatedUser.preferredLocation = this.state.preferredLocation;

        if (isNew)
            updatedUser.enabled = true;  //new users are enabled by default

        this._okCallback(updatedUser, isNew);  //false if editing an existing user, true if creating a new user
    }

    _cancelButtonPressed = () => {
        this._cancelCallback();
    }
    

    _permissionsCheckboxChanged = (which, isSelected) => {
        let newBits;
        if (isSelected)
            newBits = PermissionSet.setPermission(this.state.permissionBits, which);
        else
            newBits = PermissionSet.clearPermission(this.state.permissionBits, which);
        this.setState({permissionBits: newBits});
    }
    
    _setDefaultPermissionsForRole = (role) => {
        const newBits = PermissionSet.defaultPermissionsForRole(role.enumName);
        this.setState({permissionBits: newBits});
    }

    _changeRole = (role) => {
        this.setState({role: role});
        this._setDefaultPermissionsForRole(role);
    }

    _databaseMenuItems = () => {
                
        const availableDatabases = Global.user.databases;  //can add any that the logged in user belongs to

        let menuItems = [];
        availableDatabases.forEach((database) => {
            
            if (database !== 'admin') {
                const existingDatabases = this.state.databases;

                if (!existingDatabases || !existingDatabases.includes(database)) {
                    menuItems.push({label: database, 
                                    selectCallback: () => {this._addDatabase(database);}}
                                   );             
                }  
            }
        });
            
        return menuItems;
    };

    _addDatabase = (database) => {      
        this.setState({databases: [...this.state.databases, database]});
    }
    
    _removeDatabase = (databaseToRemove) => {
        this.setState({databases: this.state.databases.filter((database) => database !== databaseToRemove) });
    }
    
    render() {
        
        let maxWidth = window.innerWidth * 0.85;

        const title = this.props.user ? ("Edit User \"" + this.props.user.fullName() + "\"") : "Create New User";
        const okText = this.props.user ? "Ok" : "Create";
        
        const validValues = true;

        let roles = Object.keys(AgsUser.Role).map(key => AgsUser.Role[key]);
        
        // Filter out roles that the user cannot assign
        roles = roles.filter((role) => { return AgsUser.canAssignRole(Global.user.role, role); });

        const selectedPermissions = PermissionSet.getPermissionsArray(this.state.permissionBits);


        const hasAllDatabases = this.state.role === AgsUser.Role.AGENT || this.state.role === AgsUser.Role.SUPERADMIN;
        const addDatabaseMenuItems = this._databaseMenuItems();

        const permissionsFixed = this.state.role === AgsUser.Role.SUPERADMIN || 
                                 this.state.role === AgsUser.Role.OBSERVER ||
                                 this.state.role === AgsUser.Role.KIOSK;
        
        const addDatabaseIconColor = addDatabaseMenuItems.length > 0 ? 'gray' : '#EEEEEE'; 

        const coloredCircle = (color) => {
            return <span style={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            backgroundColor: color,
                        }}>
                    </span>;
        }

        return (
            <Popover open={this.props.isOpen} 
                anchorReference="anchorPosition"
                anchorPosition={{left: window.innerWidth/2, top: window.innerHeight/2}}
                transformOrigin={{vertical: 'center', horizontal: 'center'}}  >
                
                <div style={{margin: 20, width: maxWidth}}>
                    <Typography variant="subtitle1" style={{fontWeight: 'bold', marginBottom: 20}}>{title}</Typography>  

                    <Grid container spacing={2} style={{marginTop: 10, marginBottom: 20}}>
                        
                        <Grid item xs={12} md={6}>
                            <TextField variant="outlined" size="small" InputLabelProps={{ shrink: true}} label="First Name" value={this.state.firstName} onChange={(e) => this.setState({firstName: e.target.value})} fullWidth style={{marginBottom: 20}} />
                            <TextField variant="outlined" size="small" InputLabelProps={{ shrink: true}} label="Last Name" value={this.state.lastName} onChange={(e) => this.setState({lastName: e.target.value})} fullWidth style={{marginBottom: 20}} />
                            <TextField variant="outlined" size="small" InputLabelProps={{ shrink: true}} label="Email" value={this.state.email} onChange={(e) => this.setState({email: e.target.value})} fullWidth style={{marginBottom: 20}} />

                            <Autocomplete
                                    size='small'
                                    value={this.state.role}
                                    getOptionLabel={(role) => role.label }
                                    onChange={(event, newValue) => { this._changeRole(newValue); }}
                                    options={roles}
                                    blurOnSelect
                                    disableClearable
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Role"
                                            variant="outlined"
                                            InputLabelProps={{ shrink: true }}
                                            InputProps={{
                                                ...params.InputProps,
                                                startAdornment: this.state.role ? (
                                                    <div style={{ display: 'flex', alignItems: 'center', marginLeft: 10, marginRight: 10 }}>
                                                        {coloredCircle(this.state.role.color)}
                                                    </div>
                                                ) : null,
                                            }}
                                        />
                                    )}
                                    renderOption={(option) => (
                                        <div style={{display: 'flex', gap: 15, alignItems: 'center'}}>
                                            {coloredCircle(option.color)}
                                            {option.label}
                                        </div>
                                    )}
                                /> 
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField variant="outlined" size="small" InputLabelProps={{ shrink: true}} label="Profile URL" value={this.state.profileUrl} onChange={(e) => this.setState({profileUrl: e.target.value})} fullWidth style={{marginBottom: 20}} />
                            <TextField variant="outlined" size="small" InputLabelProps={{ shrink: true}} label="Preferred Location" value={this.state.preferredLocation} onChange={(e) => this.setState({preferredLocation: e.target.value})} fullWidth style={{marginBottom: 20}} />
                        </Grid>
                    </Grid>

                    <Grid container spacing={2} style={{marginTop: 10, marginBottom: 20}}>
                        
                        <Grid item xs={12} md={6}>

                            <Paper style={this.styles.paper}>
                                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'top'}}>
                                    <Typography variant="body2" style={this.styles.paperLabel}>Permissions</Typography>
                                    <Tooltip title="Select the default permissions based on the current Role">
                                        <Button onClick={() => this._setDefaultPermissionsForRole(this.state.role)} style={{marginTop: 5, marginRight: 10, marginBottom: -5}} variant="outlined" fullWidth={false} color='primary'>Set Default</Button>
                                    </Tooltip>
                                </div>
                                <div style={{marginLeft: 15}}>
                                    <PermissionCheckboxes disabled={permissionsFixed} isEmployee={Global.user.isSiteEmployee()} selected={selectedPermissions} checkboxChanged={this._permissionsCheckboxChanged}/>
                                </div>
                            </Paper>
                        </Grid>

                        <Grid item xs={12} md={6}>

                            <Paper style={this.styles.paper}>
                                <div style={{display: 'flex'}}>
                                    <Typography variant="body2" style={this.styles.paperLabel}>Databases</Typography>   
                                    
                                    {!hasAllDatabases ? 
                                        
                                        <PopupMenu menuIcon={(<AddCircleOutlineIcon style={{color: addDatabaseMenuItems.length === 0 ? ThemeColors.disabledGrey : ThemeColors.addColor}}/>)}  
                                                    menuItems={addDatabaseMenuItems} 
                                                    diasbled={addDatabaseMenuItems.length === 0}
                                                    menuIconStyle={{color: addDatabaseIconColor, marginRight: 4, marginTop: -4}} 
                                                    menuTooltipText="Add access to a database"/>
                                        : null
                                    }
                                </div>
                                
                                {hasAllDatabases ? 
                                    <Typography style={this.styles.allDatabases} variant="body2">This Role has access to all databases</Typography> 
                                    :
                                    <List dense>
                                        {this.state.databases.map((database, index) =>
                                            (<div key={index}>
                                                {index > 0 ? <Divider style={this.styles.divider}/> : null  /*Dividers above each item except the first*/}
                                                <ListItem>       
                                                    <ListItemText primary={database}/>
                                                    <ListItemSecondaryAction>
                                                        <Tooltip title={"Remove user's access to " + database}>
                                                            <IconButton edge="end" aria-label="delete" onClick={() => {this._removeDatabase(database)}}>
                                                                <RemoveCircleOutlineIcon/>
                                                            </IconButton>
                                                        </Tooltip>
                                                    </ListItemSecondaryAction>
                                                </ListItem>
                                                </div>)                       
                                        )}
                                    </List>
                                }
                            </Paper>
                        </Grid>
                    </Grid>


                    <Container style={this.styles.container}>    
                        <Button onClick={this._okButtonPressed} disabled={!validValues} color="primary" variant="outlined" style={this.styles.button}>{okText}</Button>
                        <Button onClick={this._cancelButtonPressed} variant="outlined" style={this.styles.button}>Cancel</Button>
                    </Container>

                    <Container style={this.styles.container}>    
                        <Typography variant="body2" style={{color: 'gray', fontStyle: 'italic'}}>Changes to User may take several minutes to propagate to other portals</Typography>
                    </Container>
                </div>
            </Popover>
        );
        
        
    }


}